<template>
  <div class="lineup-page">
    <h1>IABF {{ festivalYear }} Showcase Lineup</h1>

    <button
      v-if="playingPieceId || playingCorpsName"
      type="button"
      class="stop-all-btn"
      @click="stopAllMusic"
    >
      ⏹ Stop music
    </button>

    <div v-if="!showActive && !loading" class="search-row">
      <input
        v-model="search"
        type="search"
        placeholder="Search by dancer name…"
        class="search-input"
      />
      <button v-if="search" type="button" class="clear-btn" @click="search = ''">Clear</button>
    </div>

    <div v-if="loading" class="muted">Loading…</div>

    <!-- In-progress view: compact, focused on where we are in the show -->
    <template v-else-if="showActive">
      <div v-if="completedSlots.length" class="completed-bar">
        <button class="completed-toggle" type="button" @click="showCompleted = !showCompleted">
          ✓ {{ completedSlots.length }} completed
          <span class="caret">{{ showCompleted ? '▴' : '▾' }}</span>
        </button>
        <ol v-if="showCompleted" class="runner-slot-list completed-list">
          <li
            v-for="slot in completedSlots"
            :key="'done-' + slot.key"
            :class="['kind-' + slot.kind, 'is-done']"
          >
            <div class="slot-main">
              <span v-if="slot.block" class="block-chip" :class="'chip-' + slot.block">Block {{ slot.block }}</span>
              <span v-else-if="slot.kind === 'corps'" class="block-chip chip-corps">Corps</span>
              <span class="slot-label">{{ slot.label }}</span>
              <span v-if="slot.subtitle" class="slot-sub">{{ slot.subtitle }}</span>
            </div>
          </li>
        </ol>
      </div>

      <ol class="runner-slot-list">
        <li
          v-for="slot in visibleSlots"
          :key="slot.key"
          :class="[
            'kind-' + slot.kind,
            slot.block ? 'block-' + slot.block : '',
            { 'is-current': slot.key === currentSlotKey }
          ]"
        >
          <div class="slot-main">
            <span v-if="slot.block" class="block-chip" :class="'chip-' + slot.block">Block {{ slot.block }}</span>
            <span v-else-if="slot.kind === 'corps'" class="block-chip chip-corps">Corps</span>
            <span class="slot-label">
              {{ slot.label }}
              <span v-if="slot.kind === 'intermission' && intermissionElapsedText" class="elapsed-tag">{{ intermissionElapsedText }} elapsed</span>
            </span>
            <span v-if="slot.subtitle" class="slot-sub">{{ slot.subtitle }}</span>
            <div v-if="slot.piece && (pieceTypeLabel(slot.piece) || competitionLabel(slot.piece))" class="slot-tags">
              <span v-if="pieceTypeLabel(slot.piece)" class="type-pill" :class="pieceTypeClass(slot.piece)">{{ pieceTypeLabel(slot.piece) }}</span>
              <span v-if="competitionLabel(slot.piece)" class="comp-pill">🏅 {{ competitionLabel(slot.piece) }}</span>
            </div>
          </div>
          <span v-if="slot.key === currentSlotKey" class="now-tag-inline">NOW</span>
        </li>
      </ol>
    </template>

    <!-- Default view: pretty lineup with full program details -->
    <div v-else class="show-flow">
      <div v-if="search && noSearchMatches" class="muted">No solos match "{{ search }}".</div>
      <template v-for="(slot, i) in showcaseStructure">
        <div v-if="search && searchGapMinutes[i]" :key="'g' + i" class="flow-gap">
          ≈ {{ searchGapMinutes[i] }} min between
        </div>
        <div v-if="slot.type === 'corps' && (!search || corpsMatchesSearch(slot.name))" :key="'c' + i" class="flow-item flow-corps" :class="{ 'is-done': isDone(corpsSlotKey(slot.name)), 'is-current': isCurrent(corpsSlotKey(slot.name)) }">
          <div class="corps-header">
            <span class="flow-num">{{ i + 1 }}</span>
            <span class="flow-title">{{ slot.name }}</span>
            <template v-if="corpsTracksFor(slot.name).length === 1">
              <button
                type="button"
                class="play-btn corps-play"
                :class="{ 'is-playing': playingCorpsName === slot.name }"
                :title="playingCorpsName === slot.name ? 'Pause' : 'Play music'"
                @click="playCorpsTrack(slot.name, 0)"
              >{{ playingCorpsName === slot.name ? '⏸' : '▶' }}</button>
            </template>
            <template v-else-if="corpsTracksFor(slot.name).length > 1">
              <span class="corps-track-list">
                <button
                  v-for="(t, ti) in corpsTracksFor(slot.name)"
                  :key="ti"
                  type="button"
                  class="play-btn corps-play track-btn"
                  :class="{ 'is-playing': playingCorpsName === slot.name && corpsTrackIndex === ti }"
                  :title="`Track ${ti + 1}`"
                  @click="playCorpsTrack(slot.name, ti)"
                >{{ playingCorpsName === slot.name && corpsTrackIndex === ti ? '⏸' : '▶' }} <span class="track-num">{{ ti + 1 }}</span></button>
              </span>
            </template>
          </div>
          <div v-if="playingCorpsName === slot.name" class="player-scrub corps-scrub">
            <input
              type="range"
              min="0"
              :max="duration || 0"
              :value="currentTime"
              step="0.1"
              class="scrub-input"
              :style="{ '--scrub-pct': scrubPct }"
              @input="seek($event.target.value)"
            />
            <span class="scrub-time">
              <template v-if="corpsTracksFor(slot.name).length > 1">Track {{ corpsTrackIndex + 1 }} · </template>{{ fmtTime(currentTime) }} / {{ fmtTime(duration) }}
            </span>
          </div>
          <div class="corps-info">
            <div v-if="corpsSongLine(slot.name)" class="corps-song">{{ corpsSongLine(slot.name) }}</div>
            <div class="corps-choreo">Choreographed by {{ corpsChoreographerFor(slot.name) }}</div>
            <div v-for="group in (corpsCasts[slot.name] || [])" :key="group.role || 'default'" class="corps-cast-group">
              <strong v-if="group.role" class="role-label">{{ group.role }}:</strong>
              <span class="corps-cast">{{ group.dancers.join(', ') }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="slot.type === 'intermission' && (!search || searchHasMatchesAround(i))" :key="'i' + i" class="flow-item flow-intermission" :class="{ 'is-done': isDone('intermission'), 'is-current': isCurrent('intermission') }">
          <span>— 15-min Intermission —</span>
        </div>
        <div v-else-if="slot.type === 'soloBlock' && (!search || piecesInBlock(slot.block).length > 0)" :key="'b' + i" class="flow-item flow-block">
          <div class="flow-block-header" :class="{ 'is-done': isDone(blockHeaderKey(slot.block)), 'is-current': isCurrent(blockHeaderKey(slot.block)) }">
            <span class="flow-num">{{ i + 1 }}</span>
            <span class="flow-title">{{ slot.name }}</span>
            <span v-if="slot.subtitle" class="flow-subtitle">· {{ slot.subtitle }}</span>
          </div>
          <div v-if="piecesInBlock(slot.block).length === 0" class="muted">No pieces yet.</div>
          <ol v-else class="block-list">
            <li
              v-for="(d, idx) in piecesInBlock(slot.block)"
              :key="d.piece.id"
              :value="d.position"
              :class="{ 'is-done': isDone(pieceSlotKey(d.piece)), 'is-current': isCurrent(pieceSlotKey(d.piece)) }"
            >
              <span v-if="isCurrent(pieceSlotKey(d.piece))" class="now-tag">NOW</span>
              <div class="piece-row">
                <div class="piece-info">
                  <div class="piece-title">
                    {{ programTitleFor(d.piece) }}
                    <span v-if="search" class="position-of">· #{{ d.position }} of {{ d.total }}</span>
                  </div>
                  <div v-if="programSongLine(d.piece)" class="piece-song">{{ programSongLine(d.piece) }}</div>
                  <div v-if="programDancerFor(d.piece) || teacherCredit(d.piece)" class="piece-meta">
                    <strong v-if="programDancerFor(d.piece)">{{ programDancerFor(d.piece) }}</strong>
                    <span v-if="teacherCredit(d.piece)">
                      <template v-if="programDancerFor(d.piece)"> · </template>{{ teacherCredit(d.piece) }}
                    </span>
                  </div>
                  <div v-if="pieceTypeLabel(d.piece) || competitionLabel(d.piece)" class="piece-tags">
                    <span v-if="pieceTypeLabel(d.piece)" class="type-pill" :class="pieceTypeClass(d.piece)">{{ pieceTypeLabel(d.piece) }}</span>
                    <span v-if="competitionLabel(d.piece)" class="comp-pill">🏅 {{ competitionLabel(d.piece) }}</span>
                  </div>
                  <div v-if="playingPieceId === d.piece.id" class="player-scrub">
                    <input
                      type="range"
                      min="0"
                      :max="duration || 0"
                      :value="currentTime"
                      step="0.1"
                      class="scrub-input"
                      :style="{ '--scrub-pct': scrubPct }"
                      @input="seek($event.target.value)"
                    />
                    <span class="scrub-time">{{ fmtTime(currentTime) }} / {{ fmtTime(duration) }}</span>
                  </div>
                </div>
                <button
                  v-if="d.piece.musicUrl"
                  type="button"
                  class="play-btn"
                  :class="{ 'is-playing': playingPieceId === d.piece.id }"
                  :title="playingPieceId === d.piece.id ? 'Pause' : 'Play music'"
                  @click="togglePlay(d.piece)"
                >
                  {{ playingPieceId === d.piece.id ? '⏸' : '▶' }}
                </button>
              </div>
              <div v-if="withinBlockGapAfter(slot.block, idx)" class="within-block-gap">
                ≈ {{ withinBlockGapAfter(slot.block, idx) }} min until next
              </div>
            </li>
          </ol>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'
import { IABF } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import { resolveShowcaseStructure, resolveCorpsInfo } from '@/utils/showcase-structure'

// Real piece/corps durations come from each track's lengthInSeconds; these
// only kick in when music hasn't been uploaded yet.
const SOLO_FALLBACK_SEC = 60 * 3 + 15
const TRANSITION_SEC = 30
const BLOCK_INTRO_SEC = 30
const CORPS_FALLBACK_SEC = 4 * 60
const CORPS_TRANSITION_SEC = 60
const INTERMISSION_SEC = 15 * 60

function interleave (a, b) {
  const out = []
  const max = Math.max(a.length, b.length)
  for (let i = 0; i < max; i++) {
    if (i < a.length) out.push(a[i])
    if (i < b.length) out.push(b[i])
  }
  return out
}

function formatElapsed (ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total - h * 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function teacherCreditFor (piece) {
  const t = (piece && piece.programTeacher || '').trim()
  if (!t) return ''
  if (/(coached by|choreographed by|taught by|teacher\b|coach\b|choreographer\b|\bby \w)/i.test(t)) return t
  if (piece.pieceType === 'Classical Variation') return `coached by ${t}`
  return `choreographed by ${t}`
}

function autoOrder (pieces) {
  // Comp/non-comp alternation carries across placement-group boundaries —
  // so the last beginning piece and the first middle piece end up
  // different types whenever both sides have entries.
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of pieces) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const unspecHalf = Math.ceil(groups.unspecified.length / 2)
  const groupSequence = [
    groups.beginning,
    groups.unspecified.slice(0, unspecHalf),
    groups.middle,
    groups.unspecified.slice(unspecHalf),
    groups.end
  ]
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const out = []
  let lastType = null
  for (const list of groupSequence) {
    const comp = list.filter(isComp)
    const non = list.filter(p => !isComp(p))
    if (!comp.length && !non.length) continue
    let first, second
    if (!comp.length) { first = non; second = [] }
    else if (!non.length) { first = comp; second = [] }
    else if (lastType === 'comp') { first = non; second = comp }
    else if (lastType === 'non') { first = comp; second = non }
    else {
      first = comp.length >= non.length ? comp : non
      second = first === comp ? non : comp
    }
    const interleaved = interleave(first, second)
    out.push(...interleaved)
    if (interleaved.length) {
      lastType = isComp(interleaved[interleaved.length - 1]) ? 'comp' : 'non'
    }
  }
  return out
}

function orderBlockPieces (pieces) {
  if (!pieces.length) return []
  const pinnedNumeric = pieces
    .filter(p => Number.isFinite(p.showcaseOrder) && p.showcaseOrder >= 1)
    .slice()
    .sort((a, b) => a.showcaseOrder - b.showcaseOrder)
  const pinnedLast = pieces.filter(p => p.showcaseOrder === 'last')
  const unpinned = pieces.filter(p => !pinnedNumeric.includes(p) && !pinnedLast.includes(p))
  const N = pieces.length
  const slots = new Array(N).fill(null)
  for (const p of pinnedNumeric) {
    let target = Math.max(1, Math.min(p.showcaseOrder, N)) - 1
    while (target < N && slots[target] !== null) target += 1
    if (target >= N) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  for (const p of pinnedLast) {
    let target = N - 1
    while (target >= 0 && slots[target] !== null) target -= 1
    if (target === -1) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of unpinned) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const half = Math.ceil(groups.unspecified.length / 2)
  const placementOrder = [
    ...groups.beginning,
    ...groups.unspecified.slice(0, half),
    ...groups.middle,
    ...groups.unspecified.slice(half),
    ...groups.end
  ]
  const compQueue = placementOrder.filter(isComp)
  const nonQueue = placementOrder.filter(p => !isComp(p))
  for (let i = 0; i < N; i++) {
    if (slots[i] !== null) continue
    let prevType = null
    for (let j = i - 1; j >= 0; j--) {
      if (slots[j] !== null) { prevType = isComp(slots[j]) ? 'comp' : 'non'; break }
    }
    let nextType = null
    if (prevType === null) {
      for (let j = i + 1; j < N; j++) {
        if (slots[j] !== null) { nextType = isComp(slots[j]) ? 'comp' : 'non'; break }
      }
    }
    let wantComp
    if (prevType === 'comp') wantComp = false
    else if (prevType === 'non') wantComp = true
    else if (nextType === 'comp') wantComp = false
    else if (nextType === 'non') wantComp = true
    else wantComp = compQueue.length >= nonQueue.length
    if (wantComp && compQueue.length) slots[i] = compQueue.shift()
    else if (!wantComp && nonQueue.length) slots[i] = nonQueue.shift()
    else if (compQueue.length) slots[i] = compQueue.shift()
    else if (nonQueue.length) slots[i] = nonQueue.shift()
  }
  return slots
}

export default {
  data () {
    return {
      loading: true,
      pieces: [],
      corpsCasts: {},
      corpsSongs: {},
      corpsMusic: {},
      playingCorpsName: null,
      corpsTrackQueue: [],
      corpsTrackIndex: 0,
      currentSlotKey: null,
      intermissionStartedAt: null,
      now: Date.now(),
      tickHandle: null,
      showCompleted: false,
      unsubscribeConfig: null,
      showcaseStructure: resolveShowcaseStructure(IABF_DANCER_YEAR),
      corpsInfo: resolveCorpsInfo(IABF_DANCER_YEAR),
      playingPieceId: null,
      audio: null,
      currentTime: 0,
      duration: 0,
      search: ''
    }
  },
  async created () {
    this.$store.commit('setLayout', 'embed-layout')
    const piecesSnap = await db.collection(IABF.pieces).get()
    this.pieces = piecesSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      // Hide dancer-canceled and not-yet-approved submissions from public lineup
      .filter(p => !p.canceledByDancer && (p.approvalStatus || 'approved') === 'approved')
    // Live updates from the show runner.
    this.unsubscribeConfig = db.collection(IABF.config).doc('showcase-runtime')
      .onSnapshot(doc => {
        const data = doc.exists ? (doc.data() || {}) : {}
        this.corpsCasts = data.corpsCasts || {}
        this.corpsSongs = data.corpsSongs || {}
        this.corpsMusic = data.corpsMusic || {}
        this.currentSlotKey = data.currentSlotKey || null
        this.intermissionStartedAt = data.intermissionStartedAt || null
        this.showcaseStructure = resolveShowcaseStructure(IABF_DANCER_YEAR, data)
        this.corpsInfo = resolveCorpsInfo(IABF_DANCER_YEAR, data)
      })
    // Tick once per second so the intermission elapsed timer stays live.
    this.tickHandle = setInterval(() => { this.now = Date.now() }, 1000)
    this.loading = false
  },
  beforeDestroy () {
    if (this.unsubscribeConfig) this.unsubscribeConfig()
    if (this.tickHandle) clearInterval(this.tickHandle)
    if (this.audio) {
      try { this.audio.pause() } catch (e) { /* noop */ }
      this.audio = null
    }
  },
  computed: {
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) || IABF_DANCER_YEAR
    },
    orderedSlotKeys () {
      const keys = []
      for (const slot of this.showcaseStructure) {
        if (slot.type === 'corps') {
          keys.push(`corps:${slot.name}`)
        } else if (slot.type === 'intermission') {
          keys.push('intermission')
        } else {
          keys.push(`block-header:${slot.block}`)
          const pieces = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === slot.block))
          pieces.forEach(p => keys.push(`piece:${p.id}`))
        }
      }
      return keys
    },
    currentIndex () {
      if (!this.currentSlotKey) return -1
      return this.orderedSlotKeys.indexOf(this.currentSlotKey)
    },
    showActive () {
      return this.currentIndex >= 0
    },
    slots () {
      const out = []
      for (const slot of this.showcaseStructure) {
        if (slot.type === 'corps') {
          out.push({ key: `corps:${slot.name}`, kind: 'corps', label: slot.name, subtitle: '' })
        } else if (slot.type === 'intermission') {
          out.push({ key: 'intermission', kind: 'intermission', label: slot.name, subtitle: '' })
        } else {
          const pieces = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === slot.block))
          pieces.forEach((p, idx) => {
            const title = this.programTitleFor(p)
            const dancer = this.programDancerFor(p)
            const credit = teacherCreditFor(p)
            const subParts = []
            if (dancer) subParts.push(dancer)
            if (credit) subParts.push(credit)
            out.push({
              key: `piece:${p.id}`,
              kind: 'piece',
              block: slot.block,
              label: `${slot.block}${idx + 1}. ${title}`,
              subtitle: subParts.join(' · '),
              piece: p
            })
          })
        }
      }
      return out
    },
    runnerCurrentIndex () {
      if (!this.currentSlotKey) return -1
      return this.slots.findIndex(s => s.key === this.currentSlotKey)
    },
    completedSlots () {
      if (this.runnerCurrentIndex < 0) return []
      return this.slots.slice(0, this.runnerCurrentIndex)
    },
    visibleSlots () {
      if (this.runnerCurrentIndex < 0) return this.slots
      return this.slots.slice(this.runnerCurrentIndex)
    },
    intermissionElapsedText () {
      if (this.currentSlotKey !== 'intermission') return ''
      if (!this.intermissionStartedAt) return ''
      return formatElapsed(this.now - this.intermissionStartedAt)
    },
    noSearchMatches () {
      if (!this.search) return false
      for (const slot of this.showcaseStructure) {
        if (slot.type === 'soloBlock' && this.piecesInBlock(slot.block).length > 0) return false
        if (slot.type === 'corps' && this.corpsMatchesSearch(slot.name)) return false
      }
      return true
    },
    searchGapMinutes () {
      // For each matched flow item (except the first), how many minutes of
      // stage time elapse between the previous matched item's end and this
      // item's start. Indexed by showcaseStructure index.
      if (!this.search) return {}
      const matched = []
      let t = 0
      this.showcaseStructure.forEach((slot, i) => {
        if (slot.type === 'soloBlock') {
          const all = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === slot.block))
          t += BLOCK_INTRO_SEC
          const pieceTimes = []
          for (const piece of all) {
            const soloSec = this.soloSeconds(piece)
            pieceTimes.push({ start: t, end: t + soloSec })
            t += soloSec + TRANSITION_SEC
          }
          const dec = this.piecesInBlock(slot.block)
          if (dec.length > 0 && pieceTimes.length) {
            const positions = dec.map(d => d.position)
            const minP = Math.min(...positions)
            const maxP = Math.max(...positions)
            matched.push({
              slotIndex: i,
              startSec: pieceTimes[minP - 1].start,
              endSec: pieceTimes[maxP - 1].end
            })
          }
        } else if (slot.type === 'corps') {
          const start = t
          t += this.corpsSeconds(slot.name)
          const end = t
          t += CORPS_TRANSITION_SEC
          if (this.corpsMatchesSearch(slot.name)) {
            matched.push({ slotIndex: i, startSec: start, endSec: end, isIntermission: false })
          }
        } else if (slot.type === 'intermission') {
          const start = t
          t += INTERMISSION_SEC
          const end = t
          if (this.searchHasMatchesAround(i)) {
            matched.push({ slotIndex: i, startSec: start, endSec: end, isIntermission: true })
          }
        }
      })
      const result = {}
      for (let k = 1; k < matched.length; k++) {
        const gapSec = Math.max(0, matched[k].startSec - matched[k - 1].endSec)
        // Hide trivial transition-only gaps (e.g. the 1-min set change after
        // intermission). Anything 2 min or more is worth surfacing.
        if (gapSec < 2 * 60) continue
        result[matched[k].slotIndex] = Math.round(gapSec / 60)
      }
      return result
    },
    scrubPct () {
      if (!this.duration || this.duration <= 0) return '0%'
      return `${Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100))}%`
    },
    currentSlot () {
      const key = this.currentSlotKey
      if (!key) return null
      if (key === 'intermission') {
        return { kind: 'intermission', title: 'Intermission', subtitle: '', meta: '' }
      }
      if (key.startsWith('corps:')) {
        const name = key.slice('corps:'.length)
        const choreo = (this.corpsInfo[name] || {}).choreographer || ''
        return {
          kind: 'corps',
          title: name,
          subtitle: 'Corps piece',
          meta: choreo ? `Choreographed by ${choreo}` : ''
        }
      }
      if (key.startsWith('piece:')) {
        const pieceId = key.slice('piece:'.length)
        const piece = this.pieces.find(p => p.id === pieceId)
        if (!piece) return { kind: 'piece', title: 'On stage', subtitle: '', meta: '' }
        const dancer = this.programDancerFor(piece)
        const credit = teacherCreditFor(piece)
        const parts = []
        if (dancer) parts.push(dancer)
        if (credit) parts.push(credit)
        return {
          kind: 'piece',
          title: this.programTitleFor(piece),
          subtitle: parts.join(' · '),
          meta: ''
        }
      }
      return null
    }
  },
  methods: {
    isDone (key) {
      if (this.currentIndex < 0) return false
      const idx = this.orderedSlotKeys.indexOf(key)
      return idx !== -1 && idx < this.currentIndex
    },
    isCurrent (key) {
      return this.currentSlotKey === key
    },
    pieceSlotKey (piece) {
      return `piece:${piece.id}`
    },
    corpsSlotKey (name) {
      return `corps:${name}`
    },
    blockHeaderKey (block) {
      return `block-header:${block}`
    },
    piecesInBlock (block) {
      // Return ordered pieces decorated with their pre-filter position
      // and the block's total piece count, so the "Solo 13 of 20" hint
      // stays meaningful even when search narrows the list.
      const inBlock = this.pieces.filter(p => p.showcaseBlock === block)
      const ordered = orderBlockPieces(inBlock)
      const total = ordered.length
      const decorated = ordered.map((piece, idx) => ({ piece, position: idx + 1, total }))
      if (!this.search) return decorated
      return decorated.filter(d => this.pieceMatchesSearch(d.piece))
    },
    pieceMatchesSearch (piece) {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return true
      // Only match on dancer/cast fields. Choreographer/teacher names are
      // excluded so e.g. searching "Kelsey" doesn't surface every piece she
      // choreographed for someone else.
      const haystack = [
        piece.programDancer, piece.namedDancers
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    },
    corpsMatchesSearch (name) {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return true
      const groups = this.corpsCasts[name] || []
      for (const group of groups) {
        for (const dancer of (group.dancers || [])) {
          if (String(dancer).toLowerCase().includes(q)) return true
        }
      }
      return false
    },
    soloSeconds (piece) {
      const n = parseInt(piece && piece.lengthInSeconds, 10)
      return Number.isFinite(n) && n > 0 ? n : SOLO_FALLBACK_SEC
    },
    corpsSeconds (corpsName) {
      const entry = this.corpsMusic[corpsName]
      if (!entry) return CORPS_FALLBACK_SEC
      const tracks = Array.isArray(entry.tracks)
        ? entry.tracks
        : (entry.url ? [{ lengthInSeconds: entry.lengthInSeconds }] : [])
      let total = 0
      for (const t of tracks) {
        const n = parseInt(t && t.lengthInSeconds, 10)
        if (Number.isFinite(n) && n > 0) total += n
      }
      return total > 0 ? total : CORPS_FALLBACK_SEC
    },
    withinBlockGapAfter (block, idx) {
      if (!this.search) return 0
      const matched = this.piecesInBlock(block)
      if (idx >= matched.length - 1) return 0
      const all = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === block))
      const p1 = matched[idx].position
      const p2 = matched[idx + 1].position
      // Sum the durations of the pieces strictly between p1 and p2, plus
      // transition time after p1 and before p2.
      let gapSec = TRANSITION_SEC
      for (let i = p1; i < p2 - 1; i++) {
        gapSec += this.soloSeconds(all[i]) + TRANSITION_SEC
      }
      if (gapSec < 2 * 60) return 0
      return Math.round(gapSec / 60)
    },
    searchHasMatchesAround (intermissionIndex) {
      // Show intermission only when the searched dancer appears both before
      // and after it — that's when the gap matters for change-time math.
      let beforeMatch = false
      let afterMatch = false
      for (let i = 0; i < this.showcaseStructure.length; i++) {
        const slot = this.showcaseStructure[i]
        let matched = false
        if (slot.type === 'corps' && this.corpsMatchesSearch(slot.name)) matched = true
        if (slot.type === 'soloBlock' && this.piecesInBlock(slot.block).length > 0) matched = true
        if (!matched) continue
        if (i < intermissionIndex) beforeMatch = true
        if (i > intermissionIndex) afterMatch = true
      }
      return beforeMatch && afterMatch
    },
    programTitleFor (piece) {
      if (piece.programTitle) return piece.programTitle
      const title = piece.title || '(untitled)'
      if (piece.songArtist) return `${title} by ${piece.songArtist}`
      return title
    },
    programSongLine (piece) {
      // Sub-line showing the song identity when programTitle is the
      // choreographic display name and piece.title / songArtist carry the
      // actual song name + composer. Skipped when nothing new to add.
      if (!piece.programTitle) return ''
      const title = String(piece.title || '').trim()
      const artist = String(piece.songArtist || '').trim()
      if (!title && !artist) return ''
      const titleMatchesProgram = title && title.toLowerCase() === piece.programTitle.toLowerCase()
      if (title && artist && !titleMatchesProgram) return `${title} by ${artist}`
      if (artist) return `by ${artist}`
      if (title && !titleMatchesProgram) return title
      return ''
    },
    programDancerFor (piece) {
      return piece.programDancer || piece.namedDancers || ''
    },
    corpsChoreographerFor (name) {
      return (this.corpsInfo[name] || {}).choreographer || ''
    },
    corpsSongLine (name) {
      const song = this.corpsSongs[name] || {}
      const title = (song.title || '').trim()
      const artist = (song.artist || '').trim()
      if (!title && !artist) return ''
      if (title && artist) return `${title} by ${artist}`
      return title || artist
    },
    teacherCredit (piece) {
      return teacherCreditFor(piece)
    },
    pieceTypeLabel (piece) {
      const t = piece && piece.pieceType
      if (!t) return ''
      if (t.startsWith('Classical')) return 'Classical'
      if (t.startsWith('Original')) return 'Original Ballet'
      if (t.startsWith('Contemporary')) return 'Contemporary/Lyrical'
      if (t.startsWith('Jazz')) return 'Jazz'
      return t
    },
    pieceTypeClass (piece) {
      const label = this.pieceTypeLabel(piece)
      if (label === 'Classical') return 'type-classical'
      if (label === 'Original Ballet') return 'type-original'
      if (label === 'Contemporary/Lyrical') return 'type-contemporary'
      if (label === 'Jazz') return 'type-jazz'
      return 'type-default'
    },
    competitionLabel (piece) {
      if (!piece || !piece.competition || piece.competition === 'None') return ''
      return piece.competition
    },
    togglePlay (piece) {
      if (!piece || !piece.musicUrl) return
      if (this.playingPieceId === piece.id) {
        if (this.audio) this.audio.pause()
        this.playingPieceId = null
        return
      }
      this.ensurePlayerAudio()
      try { this.audio.pause() } catch (e) { /* noop */ }
      this.corpsTrackQueue = []
      this.corpsTrackIndex = 0
      this.playingCorpsName = null
      this.currentTime = 0
      this.duration = 0
      this.audio.src = piece.musicUrl
      this.audio.currentTime = 0
      this.audio.play().then(() => {
        this.playingPieceId = piece.id
      }).catch(() => {
        this.playingPieceId = null
      })
    },
    seek (t) {
      const n = Number(t)
      if (this.audio && Number.isFinite(n)) {
        this.audio.currentTime = n
        this.currentTime = n
      }
    },
    stopAllMusic () {
      if (this.audio) {
        try {
          this.audio.pause()
          this.audio.currentTime = 0
        } catch (e) { /* noop */ }
      }
      this.playingPieceId = null
      this.playingCorpsName = null
      this.currentTime = 0
    },
    corpsTracksFor (name) {
      const entry = this.corpsMusic[name]
      if (!entry) return []
      if (Array.isArray(entry.tracks)) return entry.tracks
      if (entry.url) return [{ url: entry.url, fileName: entry.fileName || '', lengthInSeconds: entry.lengthInSeconds || 0 }]
      return []
    },
    playCorpsTrack (name, trackIdx) {
      const tracks = this.corpsTracksFor(name)
      if (!tracks.length) return
      const idx = trackIdx || 0
      if (idx < 0 || idx >= tracks.length) return
      // Toggle: clicking the currently-playing track pauses it.
      if (this.playingCorpsName === name && this.corpsTrackIndex === idx) {
        if (this.audio) this.audio.pause()
        this.playingCorpsName = null
        return
      }
      this.ensurePlayerAudio()
      try { this.audio.pause() } catch (e) { /* noop */ }
      // Queue starts at the picked track — auto-advance still runs through
      // the remaining tracks in order.
      this.corpsTrackQueue = tracks.slice()
      this.corpsTrackIndex = idx
      this.playingPieceId = null
      this.currentTime = 0
      this.duration = 0
      this.audio.src = this.corpsTrackQueue[idx].url
      this.audio.currentTime = 0
      this.audio.play().then(() => {
        this.playingCorpsName = name
      }).catch(() => {
        this.playingCorpsName = null
      })
    },
    ensurePlayerAudio () {
      if (this.audio) return
      this.audio = new Audio()
      this.audio.addEventListener('timeupdate', () => {
        this.currentTime = this.audio.currentTime || 0
      })
      this.audio.addEventListener('loadedmetadata', () => {
        this.duration = this.audio.duration || 0
      })
      this.audio.addEventListener('ended', () => {
        // Advance corps queue if applicable.
        if (this.playingCorpsName && this.corpsTrackQueue.length > this.corpsTrackIndex + 1) {
          this.corpsTrackIndex += 1
          this.currentTime = 0
          this.duration = 0
          this.audio.src = this.corpsTrackQueue[this.corpsTrackIndex].url
          this.audio.currentTime = 0
          this.audio.play().catch(() => { this.playingCorpsName = null })
          return
        }
        this.playingPieceId = null
        this.playingCorpsName = null
        this.corpsTrackQueue = []
        this.corpsTrackIndex = 0
        this.currentTime = 0
      })
      this.audio.addEventListener('error', () => {
        this.playingPieceId = null
        this.playingCorpsName = null
      })
    },
    fmtTime (s) {
      if (!Number.isFinite(s) || s < 0) return '0:00'
      const total = Math.floor(s)
      const m = Math.floor(total / 60)
      const sec = total % 60
      return `${m}:${String(sec).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.lineup-page {
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  color: var(--iabf-ink, #1c1b1f);
  font-family: var(--iabf-font-body, inherit);
}

h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 42px;
  letter-spacing: -.02em;
  text-align: center;
  margin: 0 0 32px 0;
}

.muted {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}

.show-flow {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.flow-item {
  border-radius: 6px;
}

.flow-corps {
  background: #fbf8f3;
  padding: 14px 18px;
  border-left: 4px solid #c0a673;
}

.corps-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.corps-info {
  margin-top: 8px;
  padding-left: 42px;
  font-size: 14px;
  color: #555;
  line-height: 1.5;
}

.corps-song {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}

.corps-choreo {
  font-style: italic;
}

.corps-cast-group {
  color: var(--iabf-ink, #1c1b1f);
  margin-top: 2px;
}

.corps-header {
  position: relative;
}

.corps-play {
  margin-left: auto;
}

.corps-track-counter {
  font-size: 11px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-left: 8px;
  font-style: italic;
}

.corps-track-list {
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.track-btn {
  padding: 0 10px !important;
  font-size: 14px !important;
  min-width: 42px;
  border-radius: 20px !important;
}
.track-btn .track-num {
  font-size: 12px;
  margin-left: 3px;
  font-weight: 700;
}
.corps-scrub {
  margin: 8px 0 0 42px;
}

.role-label {
  color: #5d4037;
  margin-right: 4px;
}

.flow-subtitle {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
  font-style: italic;
  margin-left: 4px;
  font-size: 16px;
}

.flow-intermission {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  text-align: center;
  padding: 14px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-style: italic;
  font-size: 18px;
  letter-spacing: .08em;
}

.flow-block {
  background: #fff;
  padding: 18px 20px;
  border: 1px solid #ece9e2;
  border-left: 4px solid #6a3e2b;
}

.flow-block-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0ede5;
}

.flow-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: var(--iabf-font-display, Georgia, serif);
  flex-shrink: 0;
}

.flow-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 22px;
  color: var(--iabf-ink, #1c1b1f);
}

.block-list {
  list-style: decimal;
  padding-left: 28px;
  margin: 0;
}

.block-list li {
  padding: 10px 0;
  border-bottom: 1px solid #f5f3ed;
}

.block-list li:last-child {
  border-bottom: none;
}

.piece-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.3;
}

.piece-song {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  font-style: italic;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-top: 2px;
}

.position-of {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-style: italic;
  color: var(--iabf-ink-faint, #8a8792);
  margin-left: 4px;
}

.flow-gap {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-style: italic;
  color: var(--iabf-ink-faint, #8a8792);
  text-align: center;
  padding: 6px 0;
  margin: 0 0 8px;
  letter-spacing: 0.04em;
}

.within-block-gap {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  font-style: italic;
  color: var(--iabf-ink-faint, #8a8792);
  text-align: center;
  margin-top: 8px;
  letter-spacing: 0.03em;
}

.piece-meta {
  font-size: 15px;
  color: #555;
  margin-top: 4px;
  line-height: 1.5;
}

/* In-progress view (mirrors the show runner UI) */
.completed-bar {
  margin-bottom: 10px;
}

.completed-toggle {
  background: transparent;
  border: 1px dashed #d0cbc4;
  border-radius: 6px;
  padding: 6px 14px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  cursor: pointer;
  width: 100%;
  text-align: center;
}

.completed-toggle:hover {
  color: var(--iabf-ink-muted, #5c5a63);
  border-color: var(--iabf-ink-faint, #8a8792);
}

.completed-toggle .caret {
  margin-left: 6px;
}

.completed-list {
  margin-top: 8px;
}

.runner-slot-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 6px;
  overflow: hidden;
}

.runner-slot-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f1eee9;
  gap: 12px;
}

.runner-slot-list li:last-child {
  border-bottom: none;
}

.runner-slot-list li.is-done {
  background: #fafaf8;
  color: var(--iabf-ink-faint, #8a8792);
}

.runner-slot-list li.is-done .slot-label {
  text-decoration: line-through;
}

.runner-slot-list li.is-current {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  padding-left: 10px;
}

.runner-slot-list li.kind-intermission {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  font-style: italic;
  text-align: center;
  justify-content: center;
}

/* When intermission is the current slot the background flips to light yellow,
   so put the text back to dark for legibility. */
.runner-slot-list li.kind-intermission.is-current {
  color: var(--iabf-ink, #1c1b1f);
}

.runner-slot-list li.kind-intermission.is-current .slot-label {
  color: var(--iabf-ink, #1c1b1f);
}

.runner-slot-list li.kind-corps {
  background: #fbf8f3;
}

.runner-slot-list .slot-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.runner-slot-list .block-chip {
  display: inline-block;
  align-self: flex-start;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 4px;
}

.chip-AA { background: #e3f2fd; color: #0d47a1; }
.chip-A  { background: #ede7f6; color: #4527a0; }
.chip-B  { background: #e0f2f1; color: #00695c; }
.chip-C  { background: #fff3e0; color: #e65100; }
.chip-D  { background: #fce4ec; color: #ad1457; }
.chip-corps { background: #efebe9; color: #5d4037; }

.runner-slot-list .slot-label {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
}

.runner-slot-list .slot-sub {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
}

.runner-slot-list .is-current .slot-sub {
  color: #5d4037;
  opacity: 0.85;
}

.now-tag-inline {
  font-size: 11px;
  font-weight: 700;
  background: #f9a825;
  color: var(--iabf-ink, #1c1b1f);
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: .05em;
  flex-shrink: 0;
}

.elapsed-tag {
  display: inline-block;
  margin-left: 10px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 16px;
  font-weight: 700;
  background: #f9a825;
  color: var(--iabf-ink, #1c1b1f);
  padding: 2px 10px;
  border-radius: 6px;
  vertical-align: middle;
}

.piece-tags,
.slot-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.piece-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.piece-info {
  flex: 1;
  min-width: 0;
}

.play-btn {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid var(--iabf-primary, #8a957d);
  background: #fff;
  color: var(--iabf-ink, #1c1b1f);
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-top: 4px;
  transition: background .15s, color .15s, transform .15s;
}

.play-btn:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.play-btn.is-playing {
  background: #f9a825;
  border-color: #f9a825;
  color: var(--iabf-ink, #1c1b1f);
}

.player-scrub {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.scrub-input {
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(to right, #f9a825 0%, #f9a825 var(--scrub-pct, 0%), #e0d8c8 var(--scrub-pct, 0%), #e0d8c8 100%);
  outline: none;
  cursor: pointer;
}

.scrub-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #f9a825;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  cursor: pointer;
}

.scrub-input::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #f9a825;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  cursor: pointer;
}

.scrub-time {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--iabf-ink-muted, #5c5a63);
  white-space: nowrap;
  min-width: 76px;
  text-align: right;
}

.stop-all-btn {
  position: sticky;
  top: 12px;
  z-index: 20;
  display: block;
  margin: 0 0 16px auto;
  background: #d32f2f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(211, 47, 47, 0.4);
}

.stop-all-btn:hover {
  background: #b71c1c;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
}

.search-input {
  flex: 1;
  padding: 10px 14px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  border: 1px solid #d0cbc4;
  border-radius: 6px;
  background: #fff;
  color: var(--iabf-ink, #1c1b1f);
}

.search-input:focus {
  outline: none;
  border-color: var(--iabf-primary, #8a957d);
}

.clear-btn {
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  font-family: var(--iabf-font-body, inherit);
}

.clear-btn:hover {
  background: #f5f5f5;
}

.type-pill,
.comp-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.type-pill { background: #eceff1; color: #455a64; }
.type-classical    { background: #e8eaf6; color: #283593; }
.type-original     { background: #e0f2f1; color: #00695c; }
.type-contemporary { background: #f3e5f5; color: #6a1b9a; }
.type-jazz         { background: #ffe0b2; color: #e65100; }
.type-default      { background: #eceff1; color: #455a64; }
.comp-pill { background: #fff8e1; color: #6a4e00; }

.is-done {
  opacity: 0.45;
}

.is-done .piece-title,
.is-done .flow-title {
  text-decoration: line-through;
}

.is-current {
  background: #fff8e1 !important;
  border-left: 4px solid #f9a825 !important;
}

.block-list li.is-current {
  border-radius: 4px;
  padding-left: 12px;
}

.now-tag {
  display: inline-block;
  background: #f9a825;
  color: var(--iabf-ink, #1c1b1f);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: .05em;
  margin-right: 8px;
  vertical-align: middle;
}

@media print {
  .lineup-page { padding: 20px; }
  .flow-block, .flow-corps { break-inside: avoid; }
  .completed-bar, .now-tag-inline { display: none; }
}
</style>
